import { money, tierClass } from "../api.js";
import {
  explainLeads,
  jobLabel,
  ladderLabel,
  moneyRange,
  pct,
  scoreLabel,
  ticketLabel,
  tip,
  tierLabel,
} from "../ux.js";

function personCell(en, ownerFallback) {
  const p = en.person || {};
  const name = p.display_name || en.contact_name || ownerFallback || "";
  const phones = (p.phones || []).map((x) => x.value).filter(Boolean);
  const emails = (p.emails || []).map((x) => x.value).filter(Boolean);
  const phone = en.phone || phones[0] || "";
  const email = en.email || emails[0] || "";
  const likes = (p.likely_likes || []).slice(0, 2);
  const tips = (p.talking_points || []).slice(0, 2);

  if (!name && !phone && !email) {
    return `<span class="muted">No opt-in contact yet<br/><a href="#/capture">Share capture form</a></span>`;
  }
  return `
    <div class="person-card">
      <strong>${name || "Contact"}</strong><br/>
      <span class="muted">${phone ? "☎ " + phone : "☎ —"} · ${email ? "✉ " + email : "✉ —"}</span>
      ${likes.length ? `<div class="person-tag like">Talk about: ${likes.join("; ")}</div>` : ""}
      ${tips.length ? `<div class="muted" style="margin-top:.25rem">${tips.join(" · ")}</div>` : ""}
    </div>`;
}

function sellCell(rec) {
  const lab = ladderLabel(rec.ladder_focus);
  const items = (rec.line_items || [])
    .slice(0, 4)
    .map((li) => `<li>${li.qty}× ${li.name}</li>`)
    .join("");
  const chance =
    rec.close_probability != null
      ? pct(rec.close_probability)
      : "—";
  return `
    <div class="sell-block">
      <div>
        <span class="badge b-blue" title="${lab.tip}">${lab.full}</span>
        <strong>${rec.primary_brand_name || ""}</strong>
      </div>
      <div class="muted">${rec.job_label || jobLabel(rec.job_type)}</div>
      <ul class="compact">${items}</ul>
      <div class="money-line">
        <strong>${moneyRange(rec.rev_low, rec.rev_high)}</strong>
        <span class="muted">installed (est.)</span>
      </div>
      <div class="muted small">
        Likely from this home: <strong>${money(rec.expected_rev_mid)}</strong>
        · chance ${chance}
        ${rec.ticket_band ? ` · ${ticketLabel(rec.ticket_band)}` : ""}
      </div>
    </div>`;
}

export function renderPack(pack) {
  const mkt = pack._market;
  const roll = pack.product_rollup || {};
  const mktLine = mkt
    ? `Showing <strong>${mkt.name || mkt.id}</strong> · ${pack.lead_count || 0} leads` +
      (mkt.filtered_from != null ? ` (filtered from ${mkt.filtered_from} nationwide)` : "")
    : pack.territory_id === "national"
      ? `Nationwide · ${pack.lead_count || 0} leads · use market filter in CRM if available`
      : "";

  const cards = (pack.leads || [])
    .map((L, i) => {
      const en = L.enrichment || {};
      const rec = L.recommendation || {};
      const viv = L.viability || {};
      const t = tierLabel(L.tier);
      const why = (L.reasons || []).slice(0, 3);
      return `
      <article class="lead-card">
        <header class="lead-card-head">
          <div class="lead-card-num">#${i + 1}</div>
          <div>
            <span class="badge ${tierClass(L.tier)}" title="${t.tip}">${t.short}</span>
            <span class="muted small" title="Fit score 0–1">${scoreLabel(L.score)} · ${Number(L.score || 0).toFixed(2)}</span>
          </div>
        </header>
        <div class="lead-card-grid">
          <div>
            <div class="addr">${L.address || ""}</div>
            <div class="muted">${L.city || ""}, ${L.state || ""} ${L.zip || ""}</div>
            <div class="muted small">
              Lot ${(L.lot_sqft || 0).toLocaleString()} sqft
              · Pool: ${L.pool === "yes" ? "Yes" : L.pool === "no" ? "No" : "Unknown — verify"}
              · Value proxy ${money(L.sale_price)}
            </div>
            ${
              en.market_name || rec.market_name
                ? `<div class="muted small">Market: ${en.market_name || rec.market_name}</div>`
                : ""
            }
            ${why.length ? `<ul class="why-list">${why.map((w) => `<li>${w}</li>`).join("")}</ul>` : ""}
            ${viv.property_class ? `<span class="badge b-gray">${viv.property_class}</span>` : ""}
          </div>
          <div>
            <div class="field-label">Contact</div>
            ${personCell(en, L.owner_name)}
          </div>
          <div>
            <div class="field-label">What to sell</div>
            ${sellCell(rec)}
          </div>
        </div>
        ${
          L.pitch
            ? `<div class="lead-pitch muted"><strong>Talking point:</strong> ${L.pitch}</div>`
            : ""
        }
      </article>`;
    })
    .join("");

  return `
    <h1>Leads &amp; packages</h1>
    <p class="sub">${pack.lead_count || 0} homes ready · ${mktLine || pack.territory_id || ""}</p>

    ${
      mkt && (pack.lead_count || 0) === 0
        ? tip(
            "No leads in this market yet",
            "Try another market, or clear the market filter to see the full national pack. " +
              "Your team can refresh homes for this metro on the next monthly data run."
          )
        : ""
    }

    ${explainLeads()}

    <div class="kpis compact-kpis">
      <div class="kpi"><b>${pack.lead_count || 0}</b><span>Leads</span></div>
      <div class="kpi"><b>${money(roll.avg_package_mid)}</b><span>Typical ticket</span></div>
      <div class="kpi"><b>${money(roll.expected_pipeline_rev)}</b><span>Likely revenue</span></div>
    </div>

    <div class="toolbar">
      <a class="btn primary" href="#/review">Review on Street View</a>
      <a class="btn" href="#/ladder">Revenue breakdown</a>
      <a class="btn" href="#/pitch">Pitch kit</a>
    </div>

    <div class="lead-list">
      ${cards || `<div class="panel muted">No leads in this view.</div>`}
    </div>

    ${tip(
      "About contacts & privacy",
      "Phone and email appear only from your Capture form opt-ins or licensed data you import. " +
        "“Likes / talking points” are neighborhood & property hints — not social media scrapes."
    )}
  `;
}
